// @flow
import {
  NotificationMessage,
  Sidebar,
  SidebarSection,
  toastError,
  toastSuccess,
} from 'bench-internal-theme';
import { getClientId, mapDataWithIdAndNameToOptions } from 'bk-web-app';
import React, { Component } from 'react';
import { Mutation, Query } from '@apollo/client/react/components';
import { WrappedSpinner } from 'spinner';
import ClientRulesForm from './ClientRulesForm';

import {
  CREATE_CLIENT_RULE,
  GET_CLIENT_RULE,
  GET_LEDGER_ACCOUNTS,
  UPDATE_CLIENT_RULE,
} from './queries';

const buildClientRuleVariables = (
  clientId: string,
  clientRuleData: Object,
  clientRuleId?: string,
) => ({
  id: clientRuleId,
  clientRule: {
    clientId,
    ledgerAccountId: clientRuleData.ledgerAccountId.value,
    vendorContains: clientRuleData.vendorContains,
    priority: clientRuleData.priority,
    minAmount: Number.isFinite(clientRuleData.minAmount) ? clientRuleData.minAmount : undefined,
    maxAmount: Number.isFinite(clientRuleData.maxAmount) ? clientRuleData.maxAmount : undefined,
    sourceLedgerAccountId: clientRuleData.sourceLedgerAccountId
      ? clientRuleData.sourceLedgerAccountId.value
      : undefined,
  },
});

const handleCreateClientRuleOnComplete = incrementKey => {
  incrementKey();
  toastSuccess('Client Rule Created', `Your new client rule has been added.`);
};

const handleCreateClientRuleOnError = error => {
  toastError(
    'Client Rule Not Created',
    `An error was encountered. The error was: ${error.message}`,
  );
};

const handleUpdateClientRuleOnComplete = () => {
  toastSuccess('Client Rule Updated', `Your changes have been applied.`);
};

const handleUpdateClientRuleOnError = error => {
  toastError(
    'Client Rule Not Updated',
    `An error was encountered. The error was: ${error.message}`,
  );
};

type ClientRulesSidebarContainerProps = {
  clientRuleId?: string,
  dismiss: () => any,
};

type ClientRulesSidebarContainerState = {
  clientRuleId?: string,
  key: number,
};

class ClientRulesSidebarContainer extends Component<
  ClientRulesSidebarContainerProps,
  ClientRulesSidebarContainerState,
> {
  static defaultProps = {
    clientRuleId: null,
  };

  constructor(props: ClientRulesSidebarContainerProps) {
    super(props);
    this.state = {
      key: 1,
    };
  }

  incrementKey = () => {
    this.setState({ key: this.state.key + 1 });
  };

  createOrUpdateClientRuleOnSubmit = (
    clientId: string,
    clientRuleData: Object,
    createOrUpdateClientRule: Object => Promise<any>,
    clientRuleId?: string,
  ): Promise<Object> =>
    createOrUpdateClientRule({
      variables: buildClientRuleVariables(clientId, clientRuleData, clientRuleId),
    });

  render() {
    const clientId = getClientId();
    const { clientRuleId, dismiss } = this.props;
    const { key } = this.state;
    return (
      <Query query={GET_LEDGER_ACCOUNTS} variables={{ clientId }}>
        {(getLedgerAccounts: any) => {
          if (getLedgerAccounts.loading) {
            return (
              <Sidebar dismiss={dismiss} headerContent="">
                <WrappedSpinner />
              </Sidebar>
            );
          }

          if (getLedgerAccounts.error) {
            return (
              <Sidebar dismiss={dismiss} headerContent="Error">
                <SidebarSection>
                  <NotificationMessage data-test-id="client-rule-ledger-error" styleType="error">
                    There was a problem fetching the ledgers for this client. The error was “
                    {getLedgerAccounts.error.message}”
                  </NotificationMessage>
                </SidebarSection>
              </Sidebar>
            );
          }

          const hasLedgerAccounts = getLedgerAccounts.data && getLedgerAccounts.data.ledgerAccounts;
          const ledgerAccountOptions = hasLedgerAccounts
            ? mapDataWithIdAndNameToOptions(getLedgerAccounts.data.ledgerAccounts)
            : [];
          const functionalLedgers =
            hasLedgerAccounts &&
            getLedgerAccounts.data.ledgerAccounts.filter(ledger => ledger.role);
          const functionalLedgerOptions =
            hasLedgerAccounts && functionalLedgers
              ? mapDataWithIdAndNameToOptions(functionalLedgers)
              : [];

          if (clientRuleId) {
            return (
              <Query query={GET_CLIENT_RULE} variables={{ id: clientRuleId }}>
                {(getClientRule: any) => {
                  if (getClientRule.loading) {
                    return (
                      <Sidebar dismiss={dismiss} headerContent="">
                        <WrappedSpinner />
                      </Sidebar>
                    );
                  }

                  if (getClientRule.error) {
                    return (
                      <Sidebar dismiss={dismiss} headerContent="Error">
                        <SidebarSection>
                          <NotificationMessage
                            data-test-id="client-rule-query-error"
                            styleType="error"
                          >
                            There was a problem loading the client rule. The error was “
                            {getClientRule.error.message}”
                          </NotificationMessage>
                        </SidebarSection>
                      </Sidebar>
                    );
                  }
                  const { clientRule } = getClientRule.data;
                  return (
                    <Mutation
                      mutation={UPDATE_CLIENT_RULE}
                      onCompleted={handleUpdateClientRuleOnComplete}
                      onError={handleUpdateClientRuleOnError}
                    >
                      {updateClientRule => (
                        <Sidebar dismiss={dismiss} headerContent="Edit Categorization Rule">
                          <ClientRulesForm
                            data-test-id="edit-client-rule"
                            clientRule={clientRule}
                            dismiss={dismiss}
                            functionalLedgerOptions={functionalLedgerOptions}
                            ledgerAccountOptions={ledgerAccountOptions}
                            onSubmit={clientRuleData =>
                              this.createOrUpdateClientRuleOnSubmit(
                                clientId,
                                clientRuleData,
                                updateClientRule,
                                clientRuleId,
                              )
                            }
                          />
                        </Sidebar>
                      )}
                    </Mutation>
                  );
                }}
              </Query>
            );
          }
          return (
            <Mutation
              mutation={CREATE_CLIENT_RULE}
              onCompleted={() => handleCreateClientRuleOnComplete(this.incrementKey)}
              onError={handleCreateClientRuleOnError}
            >
              {createClientRule => (
                <Sidebar dismiss={dismiss} headerContent="Create Categorization Rule">
                  <ClientRulesForm
                    data-test-id="create-client-rule"
                    dismiss={dismiss}
                    key={key}
                    functionalLedgerOptions={functionalLedgerOptions}
                    ledgerAccountOptions={ledgerAccountOptions}
                    onSubmit={clientRuleData =>
                      this.createOrUpdateClientRuleOnSubmit(
                        clientId,
                        clientRuleData,
                        createClientRule,
                      )
                    }
                  />
                </Sidebar>
              )}
            </Mutation>
          );
        }}
      </Query>
    );
  }
}

export default ClientRulesSidebarContainer;
